// MST
import { types, flow, cast, detach } from 'mobx-state-tree'

// Interfaces
import {
  ICertificationWebpageRelevantProofApiNullResponse,
  ICertificationWebpageRelevantProofApiListResponse,
  ICertificationWebpageRelevantProofDeleteDocumentAttrs,
  ICertificationWebpageRelevantProofGetListAttrs,
  ICertificationWebpageRelevantProofUpdateAttrs,
  ICertificationWebpageRelevantProofUploadDocumentAttrs,
  ICertificationWebpageRelevantProofApiUploadDocumentResponse,
  ICertificationWebpageRelevantProofDownloadDocumentAttrs,
  ICertificationWebpageRelevantProofApiDownloadDocumentResponse,
  ICertificationWebpageRelevantProofApiUpdateAttrs,
} from 'interfaces/webpage/certification/registration/precondition'
import {
  ISharedCommonFileAnswerAttrs,
  ISharedCommonLoadingAttrs,
  ISharedCommonLoadingQuestionSetAttrs,
  ISharedCommonUpdateAnswerAttrs,
} from 'interfaces/shared/shared-common.interface'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import {
  withHelper_questionSetModel,
  withHelper_commonLoadingModel,
  withHelper_commonMapLoading,
  withHelper_commonMapQuestionSetLoading,
  withHelper_commonMapAnswerValue,
  withHelper_commonMapAnswerFiles,
  withHelper_commonGetPreviousAnswerValue,
  withHelper_commonMapQuestionSet,
} from 'models/extensions/helper'

// Constant
import { ASSESSMENT_FEATURES, LOADING, LOADING_KEY_TYPE } from 'constant/shared.constant'
import { withHelper_fileDownload } from 'models/extensions/with-helper'

// Types
const { model, optional, array } = types

// Loading
const { BASE, ACTION, DOWNLOAD } = LOADING
const { ANSWER, ANSWER_FILES } = LOADING_KEY_TYPE

const CertificationWebpageRelevantProofModel = model('CertificationWebpageRelevantProof')
  .props({
    certificationWebpageRelevantProof_loading: withHelper_commonLoadingModel,
    certificationWebpageRelevantProof_list: optional(array(withHelper_questionSetModel), []),
  })
  .actions(self => ({
    certificationWebpageRelevantProof_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.certificationWebpageRelevantProof_loading, payload)

      detach(self.certificationWebpageRelevantProof_loading)
      self.certificationWebpageRelevantProof_loading = cast(map)
    },
    certificationWebpageRelevantProof_SET_LIST: (
      payload: ICertificationWebpageRelevantProofApiListResponse['res']['data'],
    ): void => {
      self.certificationWebpageRelevantProof_list = cast(withHelper_commonMapQuestionSet(payload))
    },
    certificationWebpageRelevantProof_MAP_LOADING_LIST: (payload: ISharedCommonLoadingQuestionSetAttrs): void => {
      const map = withHelper_commonMapQuestionSetLoading(self.certificationWebpageRelevantProof_list, payload)

      detach(self.certificationWebpageRelevantProof_list)
      self.certificationWebpageRelevantProof_list = cast(map)
    },
    certificationWebpageRelevantProof_MAP_ANSWER_LIST: (payload: ISharedCommonUpdateAnswerAttrs): void => {
      const map = withHelper_commonMapAnswerValue(self.certificationWebpageRelevantProof_list, payload)

      detach(self.certificationWebpageRelevantProof_list)
      self.certificationWebpageRelevantProof_list = cast(map)
    },
    certificationWebpageRelevantProof_MAP_ANSWER_FILE_LIST: (payload: ISharedCommonFileAnswerAttrs): void => {
      const map = withHelper_commonMapAnswerFiles(self.certificationWebpageRelevantProof_list, payload)

      detach(self.certificationWebpageRelevantProof_list)
      self.certificationWebpageRelevantProof_list = cast(map)
    },
    certificationWebpageRelevantProof_CLEAR_LIST: (): void => {
      self.certificationWebpageRelevantProof_list = cast([])
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    update: (
      payload: ICertificationWebpageRelevantProofApiUpdateAttrs,
    ): Promise<ICertificationWebpageRelevantProofApiNullResponse> => {
      return self.environment.certificationWebpageRelevantProofApi.certificationWebpageRelevantProof_update({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
    },
  }))
  .actions(self => ({
    /**
     * @description Fetch relevant proof list
     *
     * @param {ICertificationWebpageRelevantProofGetListAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageRelevantProof_fetchList: flow(function* (payload: ICertificationWebpageRelevantProofGetListAttrs) {
      self.certificationWebpageRelevantProof_SET_LOADING({ type: BASE, value: true })

      const response: ICertificationWebpageRelevantProofApiListResponse =
        yield self.environment.certificationWebpageRelevantProofApi.certificationWebpageRelevantProof_getList({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        self.certificationWebpageRelevantProof_SET_LIST(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpageRelevantProof_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Update relevant proof
     *
     * @param {ICertificationWebpageRelevantProofUpdateAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageRelevantProof_update: (payload: ICertificationWebpageRelevantProofUpdateAttrs) => {
      const { assessmentId, questionSetId, resultId, resultDetailId, currentValue, value } = payload.params

      // Map value to current state
      self.certificationWebpageRelevantProof_MAP_ANSWER_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        currentValue,
        value,
        featureName: ASSESSMENT_FEATURES.RELEVANT_PROOF,
        callbackResultDetailAnswerOptionIndex: async (_, data) => {
          // Start Base Loading
          self.certificationWebpageRelevantProof_SET_LOADING({ type: ACTION, value: true })

          const resultDetailIndex = data.findIndex(resultDetail => resultDetail.id === resultDetailId)
          const resultDetail = data[resultDetailIndex === 1 ? resultDetailIndex + 1 : resultDetailIndex - 1]

          // Start Loading
          self.certificationWebpageRelevantProof_MAP_LOADING_LIST({
            questionSetId,
            resultId,
            resultDetailId,
            type: ANSWER,
            loadingType: ACTION,
            value: true,
          })

          const response: ICertificationWebpageRelevantProofApiNullResponse = await self.update(payload)

          // Do when response success
          if (response.ok) {
            if (payload?.onSuccess) payload.onSuccess()
          }

          // Do when response failed
          if (!response.ok) {
            // Revert value to previous value
            self.certificationWebpageRelevantProof_MAP_ANSWER_LIST({
              questionSetId,
              resultId,
              resultDetailId,
              currentValue,
              value: withHelper_commonGetPreviousAnswerValue(currentValue),
            })

            if (payload?.onError) payload.onError()
          }

          // End the loading
          self.certificationWebpageRelevantProof_MAP_LOADING_LIST({
            questionSetId,
            resultId,
            resultDetailId,
            type: ANSWER,
            loadingType: ACTION,
            value: false,
          })

          // Start the other column loading
          self.certificationWebpageRelevantProof_MAP_LOADING_LIST({
            questionSetId,
            resultId,
            resultDetailId: resultDetail.id!,
            type: ANSWER,
            loadingType: ACTION,
            value: true,
          })

          if (response.ok) {
            // Remap other column
            self.certificationWebpageRelevantProof_MAP_ANSWER_LIST({
              questionSetId,
              resultId,
              resultDetailId: resultDetail?.id!,
              currentValue: resultDetail,
              value: null,
            })

            // Re update other column
            const selfUpdateResponse: ICertificationWebpageRelevantProofApiNullResponse = await self.update({
              params: { assessmentId },
              body: {
                answerCode: resultDetail.answer?.answerCode!,
                value: null,
              },
              requestConfig: {},
            })

            // Do when response failed
            if (!selfUpdateResponse.ok) {
              // Revert value to previous value
              self.certificationWebpageRelevantProof_MAP_ANSWER_LIST({
                questionSetId,
                resultId,
                resultDetailId: resultDetail.id!,
                currentValue: resultDetail,
                value: withHelper_commonGetPreviousAnswerValue(currentValue),
              })
            }

            // End the other column loading
            self.certificationWebpageRelevantProof_MAP_LOADING_LIST({
              questionSetId,
              resultId,
              resultDetailId: resultDetail.id!,
              type: ANSWER,
              loadingType: ACTION,
              value: true,
            })

            // End Base Loading
            self.certificationWebpageRelevantProof_SET_LOADING({ type: ACTION, value: false })
          }
        },
      })
    },

    /**
     * @description Upload relevant proof document
     *
     * @param {ICertificationWebpageRelevantProofUploadDocumentAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageRelevantProof_uploadDocument: flow(function* (
      payload: ICertificationWebpageRelevantProofUploadDocumentAttrs,
    ) {
      // Start Base Loading
      self.certificationWebpageRelevantProof_SET_LOADING({ type: ACTION, value: true })

      const { assessmentId, questionSetId, resultId, resultDetailId, fileId, value } = payload.params

      // Start Loading
      self.certificationWebpageRelevantProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: true,
      })

      const response: ICertificationWebpageRelevantProofApiUploadDocumentResponse =
        yield self.environment.certificationWebpageRelevantProofApi.certificationWebpageRelevantProof_uploadDocument({
          params: { assessmentId },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        // Map data when success
        self.certificationWebpageRelevantProof_MAP_ANSWER_FILE_LIST({
          questionSetId,
          resultId,
          resultDetailId,
          fileId: fileId!,
          value: { ...response.res.data, userOriginalFileName: value.name },
          type: 'edit',
        })

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      // Stop Loading
      self.certificationWebpageRelevantProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: false,
      })

      // End Base Loading
      self.certificationWebpageRelevantProof_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Delete relevant proof document
     *
     * @param {ICertificationWebpageRelevantProofDeleteDocumentAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageRelevantProof_deleteDocument: flow(function* (
      payload: ICertificationWebpageRelevantProofDeleteDocumentAttrs,
    ) {
      // Start Base Loading
      self.certificationWebpageRelevantProof_SET_LOADING({ type: ACTION, value: true })

      const { userFileTransactionId, questionSetId, resultId, resultDetailId, fileId } = payload.params

      // Start Loading
      self.certificationWebpageRelevantProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: true,
      })

      const response: ICertificationWebpageRelevantProofApiNullResponse =
        yield self.environment.certificationWebpageRelevantProofApi.certificationWebpageRelevantProof_deleteDocument({
          params: { userFileTransactionId },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        // Map data when success
        self.certificationWebpageRelevantProof_MAP_ANSWER_FILE_LIST({
          questionSetId,
          resultId,
          resultDetailId,
          fileId: fileId!,
          type: 'delete',
        })

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      // Stop Loading
      self.certificationWebpageRelevantProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: false,
      })

      // End Base Loading
      self.certificationWebpageRelevantProof_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Download document of relevant proof
     *
     * @param {ICertificationWebpageRelevantProofDownloadDocumentAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageRelevantProof_downloadDocument: flow(function* (
      payload: ICertificationWebpageRelevantProofDownloadDocumentAttrs,
    ) {
      const { blobId, questionSetId, resultId, resultDetailId, fileId } = payload.params

      // Start Loading
      self.certificationWebpageRelevantProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: DOWNLOAD,
        value: true,
      })

      const response: ICertificationWebpageRelevantProofApiDownloadDocumentResponse =
        yield self.environment.certificationWebpageRelevantProofApi.certificationWebpageRelevantProof_downloadDocument({
          params: { blobId },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        withHelper_fileDownload(response.res, response.headers!)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      // Stop Loading
      self.certificationWebpageRelevantProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: DOWNLOAD,
        value: false,
      })
    }),
  }))

export const createCertificationWebpageRelevantProofDefaultModel = () => optional(CertificationWebpageRelevantProofModel, {})
